"use client";

import SectionTitle from "@/components/ui/SectionTitle";
import { motion, Variants, useInView } from "framer-motion";
import React, { useRef } from "react";

export type ToolsStackCategory = {
    title?: string;
    items?: string[];
    isSpacer?: boolean;
};

export type ToolsStackProps = {
    title?: string;
    description?: string;
    categories?: ToolsStackCategory[];
    leftColSpanLg?: 3 | 4 | 5 | 6;
    containerClassName?: string;
    cardClassName?: string;
    pillClassName?: string;
    withBackground?: boolean;
    special5?: boolean;
};

// ---------------- Pill ----------------
const Pill: React.FC<{ label: string; className?: string }> = ({ label, className }) => (
    <span
        className={
            "inline-flex items-center rounded-lg border border-[#ccc]/32 bg-[#ededed54] px-3 py-1 text-sm text-[#1b1b1b]" +
            (className ? ` ${className}` : "")
        }
    >
        {label}
    </span>
);

// ---------------- Card ----------------
const Card: React.FC<{
    category: ToolsStackCategory;
    cardClassName?: string;
    pillClassName?: string;
}> = ({ category, cardClassName, pillClassName }) => {
    const title = category.title ?? "";
    const items = category.items ?? [];

    return (
        <div
            className={
                "relative w-full h-full bg-[#fff]/30 border border-[#ccc]/64 backdrop-blur-sm p-4 sm:p-6 shadow-[0_12px_40px_rgba(0,0,0,0.03)] transition-transform duration-300 hover:scale-[1.03]" +
                (cardClassName ? ` ${cardClassName}` : "")
            }
        >
            <h4 className="text-base sm:text-lg lg:text-xl font-bold text-[#1b1b1b]">{title}</h4>
            <div className="mt-2 sm:mt-3 lg:mt-4 flex flex-wrap gap-1 gap-y-2 sm:gap-3">
                {items
                    .filter((i) => i && i.trim() !== "")
                    .map((i, idx) => (
                        <Pill
                            key={`${title}-${i}-${idx}`}
                            label={i}
                            {...(pillClassName && { className: pillClassName })}
                        />
                    ))}
            </div>
        </div>
    );
};

// ---------------- ToolsStack ----------------
const ToolsStack: React.FC<ToolsStackProps> = ({
    title,
    description,
    categories = [],
    leftColSpanLg = 4,
    containerClassName,
    cardClassName,
    pillClassName,
    withBackground = true,
    special5 = false,
}) => {
    const numCards = categories.length;
    const leftColCls = `lg:col-span-${leftColSpanLg}`;

    // -------- Row building --------
    const rows: ToolsStackCategory[][] = [];
    if (numCards === 5 && special5) {
        rows.push(categories.slice(0, 2));
        rows.push(categories.slice(2, 4));
        rows.push(categories.slice(4, 5));
    } else if (numCards === 7) {
        rows.push(categories.slice(0, 3));
        rows.push(categories.slice(3, 7));
    } else {
        let colsPerRow = 3;
        if (numCards === 4) colsPerRow = 2;
        else if (numCards === 6) colsPerRow = 3;
        else if (numCards === 8) colsPerRow = 4;

        for (let i = 0; i < categories.length; i += colsPerRow) {
            rows.push(categories.slice(i, i + colsPerRow));
        }
    }

    // -------- Framer Motion setup --------
    const ref = useRef(null);
    const inView = useInView(ref, { once: false, margin: "-100px 0px -100px 0px" });

    const sectionVariants: Variants = {
        hidden: { opacity: 0, x: 100 },
        visible: { opacity: 1, x: 0 },
    };

    return (
        <motion.section
            ref={ref}
            variants={sectionVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}   // animate forward/back on scroll
            transition={{ duration: 0.8, ease: "easeOut" }}
            className={`py-4 sm:py-8 md:py-12 lg:py-16 xl:py-20 2xl:py-24 relative ${
                withBackground ? "bg-gray-100/50" : ""
            }`}
        >
            <div className={"custom-container " + (containerClassName ? ` ${containerClassName}` : "")}>
                <div className="grid gap-8 xl:gap-16 lg:grid-cols-12 items-center">
                    {/* Left Column */}
                    <div className={`${leftColCls} mt-8 lg:mt-16`}>
                        {title && (
                            <SectionTitle
                                title={title}
                                subtitle=""
                                description={description || ""}
                                align="left"
                            />
                        )}
                    </div>

                    {/* Right Column */}
                    <div className={`lg:col-span-${12 - Number(leftColSpanLg)} relative z-10`}>
                        {/* Desktop */}
                        {rows.map((row, rowIdx) => {
                            let shiftClass = "";
                            if (numCards === 4 || numCards === 6)
                                shiftClass = rowIdx === 0 ? "lg:-translate-x-10" : "lg:translate-x-10";
                            else if (numCards === 8)
                                shiftClass = rowIdx === 0 ? "lg:-translate-x-8" : "lg:translate-x-8";
                            else if (numCards === 7)
                                shiftClass = rowIdx === 0 ? "lg:-translate-x-12" : "lg:translate-x-6";

                            return (
                                <div
                                    key={rowIdx}
                                    className={`hidden md:grid gap-4 relative z-10 transform transition-transform duration-300 ${shiftClass} ${
                                        rowIdx === rows.length - 1 ? "pb-0" : "pb-5"
                                    }`}
                                    style={{
                                        gridTemplateColumns:
                                            special5 && numCards === 5
                                                ? "repeat(2, minmax(0, 1fr))"
                                                : `repeat(${row.length}, minmax(0, 1fr))`,
                                    }}
                                >
                                    {row.map((cat, idx) => {
                                        const isLastSpecial5 =
                                            special5 && numCards === 5 && rowIdx === 2 && idx === 0;

                                        return (
                                            <div
                                                key={`cat-${rowIdx}-${idx}`}
                                                className="h-full"
                                                style={isLastSpecial5 ? { gridColumn: "1 / -1" } : undefined}
                                            >
                                                <Card
                                                    category={cat}
                                                    {...(cardClassName && { cardClassName })}
                                                    {...(pillClassName && { pillClassName })}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}

                        {/* Mobile */}
                        <div className="md:hidden grid grid-cols-1 gap-4 mb-6 relative z-10">
                            {categories.map((cat, idx) => (
                                <Card
                                    key={`cat-${idx}`}
                                    category={cat}
                                    {...(cardClassName && { cardClassName })}
                                    {...(pillClassName && { pillClassName })}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </motion.section>
    );
};

export default ToolsStack;